import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { ApiContext } from "../../../../App";
import { GROUP_TYPE_TO_TEXT } from "./MeteringGroupEditPopUp";

// Controller device types the scheduler pulls power readings from, with the
// identifier format each expects. The API doesn't check the format.
export const IDENTIFIER_FORMATS = {
    emporia: "1",
    shelly: "em_<gateway>_<0-2>, pro3em_<gateway>_<total|a|b|c>",
    shelly_fm: "<type>-<mac>_<triphase|monophase>_<selector>",
};

export const controllerId = (controller) =>
    Number(controller.url.split("/").slice(-2)[0]);

function PointEditForm(props) {
    const { value, onChange, errorMessages, controllers, tariffs, groups } = props;

    const { t } = useTranslation();

    const textField = (id, label, extraProps = {}) => (
        <TextField
            variant="standard"
            id={id}
            label={label}
            value={value[id] ?? ""}
            onChange={(event) => onChange(id, event.target.value)}
            error={errorMessages.hasOwnProperty(id)}
            helperText={errorMessages[id] ? errorMessages[id] : ""}
            {...extraProps}
        />
    );

    const controller = controllers.find((c) => controllerId(c) === value?.controller);
    const format = IDENTIFIER_FORMATS[controller?.device_type];

    // Sorted by type so the options can be grouped under type headings.
    const typeOrder = Object.keys(GROUP_TYPE_TO_TEXT);
    const groupOptions = [...groups].sort(
        (a, b) =>
            typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type) ||
            a.name.localeCompare(b.name),
    );

    return value ? (
        <Stack spacing={2}>
            {textField("name", t("Name"))}
            {textField("controller", t("Controller"), {
                select: true,
                children: controllers.map((c) => (
                    <MenuItem key={c.url} value={controllerId(c)}>
                        {c.name}
                    </MenuItem>
                )),
                helperText:
                    errorMessages.controller ??
                    (controllers.length === 0
                        ? t(
                              "This facility has no controllers that support metering-related readings.",
                          )
                        : ""),
            })}
            {textField("identifier", t("Identifier"), {
                helperText: errorMessages.identifier ?? format ?? "",
            })}
            {textField("tariff", t("Tariff"), {
                select: true,
                children: tariffs.map((tariff) => (
                    <MenuItem key={tariff.id} value={tariff.id}>
                        {tariff.name}
                    </MenuItem>
                )),
            })}
            <Autocomplete
                multiple
                disableCloseOnSelect
                options={groupOptions}
                groupBy={(group) => t(GROUP_TYPE_TO_TEXT[group.type] ?? group.type)}
                getOptionLabel={(group) => group.name}
                value={groupOptions.filter((group) =>
                    (value.groups ?? []).includes(group.id),
                )}
                onChange={(event, newValue) =>
                    onChange(
                        "groups",
                        newValue.map((group) => group.id),
                    )
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        label={t("Groups")}
                        error={errorMessages.hasOwnProperty("groups")}
                        helperText={errorMessages.groups ? errorMessages.groups : ""}
                    />
                )}
            />
        </Stack>
    ) : null;
}

export default function MeteringPointEditPopUp(props) {
    const { open, onCloseWithoutSaving, onCloseWithUpdate, controllers, tariffs, groups } =
        props;

    const { t } = useTranslation();

    const [data, setData] = React.useState(props.data);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [errorMessages, setErrorMessages] = React.useState({});

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const { powerSensorPointApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    const handleFormChange = (id, value) => {
        setData((old) => ({ ...old, [id]: value }));
        setErrorMessages((old) =>
            Object.fromEntries(
                Object.entries(old).filter(([key]) => key !== id),
            ),
        );
    };

    const handleCloseWithoutSaving = () => {
        setData(props.data);
        onCloseWithoutSaving();
        setErrorMessages({});
    };

    const handleSave = () => {
        setErrorMessages({});

        const payload = {
            facility: data.facility,
            name: data.name ?? "",
            controller: data.controller ?? null,
            identifier: data.identifier ?? "",
            tariff: data.tariff ?? null,
            groups: data.groups ?? [],
        };

        const onSuccess = (saved) => {
            onCloseWithUpdate(saved);
        };

        const onError = (response) => {
            setErrorMessages(response);
            const non_field_errors = Object.entries(response)
                .filter(([key]) => key === "non_field_errors")
                .map(([key, value]) => value);
            if (non_field_errors.length > 0) {
                setAlertMessage(non_field_errors.join("; "));
                setAlertOpen(true);
            }
        };

        if (data.id === undefined) {
            powerSensorPointApi.create(payload, onSuccess, onError);
        } else {
            powerSensorPointApi.update(
                `${powerSensorPointApi.baseUrl}${data.id}/`,
                payload,
                onSuccess,
                onError,
            );
        }
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleCloseWithoutSaving}
                fullScreen={fullScreen}
                fullWidth
                maxWidth={"md"}
            >
                <DialogTitle>
                    {data?.id === undefined ? t("Add Point") : t("Edit point")}
                </DialogTitle>
                <DialogContent>
                    <PointEditForm
                        value={data}
                        onChange={handleFormChange}
                        errorMessages={errorMessages}
                        controllers={controllers}
                        tariffs={tariffs}
                        groups={groups}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseWithoutSaving} autoFocus>
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleSave}>{t("Save")}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem saving data")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
